import {forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import {FieldLabel, FieldMessage} from '../lib/field';
import type {Testable} from '../lib/testable';
import {Stack} from '../Stack';

export interface SwitchProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type' | 'className'>, Testable {
	label?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	required?: boolean;
	className?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(({
	label,
	error,
	hint,
	required = false,
	disabled = false,
	id,
	testId,
	className,
	...props
}, ref) => {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const messageId = `${inputId}-message`;
	const message = error ?? hint;

	return (
		<div data-component='SwitchField' className={cn(className, 'flex flex-col')}>
			<Stack as='label' htmlFor={inputId} className={rowStyles}>
				<span className='relative inline-flex'>
					<input
						ref={ref}
						id={inputId}
						type='checkbox'
						role='switch'
						data-component='Switch'
						data-testid={testId}
						disabled={disabled}
						aria-required={required || undefined}
						aria-invalid={error ? true : undefined}
						aria-describedby={message ? messageId : undefined}
						className={cn(trackStyles, error && 'ring-1 ring-tone-red')}
						{...props}/>
					<span aria-hidden className={thumbStyles}/>
				</span>
				{label && <FieldLabel required={required} disabled={disabled}>{label}</FieldLabel>}
			</Stack>
			{message && <FieldMessage id={messageId} error={Boolean(error)}>{message}</FieldMessage>}
		</div>
	);
});

Switch.displayName = 'Switch';

const rowStyles = 'w-fit cursor-pointer has-[:disabled]:cursor-not-allowed';

const trackStyles =
	'peer h-[18px] w-[32px] shrink-0 cursor-pointer appearance-none rounded-full bg-foreground-300 shadow-s ' +
	'transition-colors checked:bg-brand-main focus-visible:outline-none ' +
	'disabled:cursor-not-allowed disabled:bg-foreground-400 disabled:checked:bg-brand-main/50';

const thumbStyles =
	'pointer-events-none absolute left-[3px] top-[3px] size-[12px] rounded-full bg-foreground-white shadow-s ' +
	'transition-transform peer-checked:translate-x-[14px] peer-disabled:bg-foreground-white/60';
